import React from "react";
import Form from 'react-bootstrap/Form';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';

import { GetApi } from "../../../api/api";
import { Roles } from "../../../api/types";
import { Feedback } from "../../../components/Validator";
import AppContext from "../../../components/AppContext";
import Button from "../../../components/Button";
import withModal, { WithModalProps, InjectedProps } from "../../../components/HOC/WithModal";
import Pending from '../../../components/Pending';

import RoleDropdown from "./RoleDropdown";
import UserTable, { ShareUsers } from "./UserTable";
import ShareUserValidator from './ShareUserValidator';


const emailId = 'emailInputShareFeatureCatalog';

interface InnerEditProps extends EditProps {
  email: string
  role: Roles
  shareUsers: ShareUsers
}

interface InnerEditState {
  email: string
  role: Roles
  shareUsers: ShareUsers
  valid: Feedback
  disabled: boolean
}

class InnerEdit extends React.Component<InnerEditProps, InnerEditState> {
  public static contextType = AppContext;

  constructor(props: InnerEditProps) {
    super(props);
    this.state = {
      email: props.email,
      role: props.role,
      shareUsers: props.shareUsers,
      valid: props.valid,
      disabled: props.disabled
    }
  }

  public render() {
    const disabledDropdown = this.state.disabled || !this.state.valid.valid
    const disabledEmail = this.state.disabled || (!this.state.valid.valid && this.state.valid.id !== emailId)
    const disabledCreate = this.state.disabled || !this.state.valid.valid || this.state.email === ''
    const disabledUserTable = this.state.disabled || !this.state.valid.valid

    const inValidEmail = this.state.valid.id === emailId;

    return (
      <Form>
        <Form.Group as={Row} controlId={emailId}>
          <Form.Label column={true} md='3'>Email</Form.Label>
          <Col md='9'>
            <Form.Control
              type='email'
              value={this.state.email}
              onChange={this.setEmail}
              disabled={disabledEmail}
              isInvalid={inValidEmail}
            />
            <Form.Control.Feedback type='invalid'>
              {this.state.valid.message}
            </Form.Control.Feedback>
            <Form.Text>
              Email address of a user to be given permission to access the feature catalog.
            </Form.Text>
          </Col>
        </Form.Group>

        <Form.Group className='d-flex'>
          <RoleDropdown
            className='ml-auto mr-2'
            role={this.state.role}
            setRole={this.setRole}
            disabled={disabledDropdown}
          />
          <Button
            variant='primary'
            onClick={this.createUserRole}
            disabled={disabledCreate}
          >
            Add user
          </Button>
        </Form.Group>

        <UserTable
          users={this.state.shareUsers}
          disabled={disabledUserTable}
          onUpdate={this.updateUserRole}
          onDelete={this.deleteUserRole}
        />
      </Form>
    )
  }

  private createUserRole = () => {
    const stateEmail = this.state.email;
    if (this.state.valid.valid
      && !(stateEmail in this.state.shareUsers)) {
      const sharePrompt = "Are you sure you want to add this user? An invitation email will be sent."
      const share = window.confirm(sharePrompt);
      if (share) {
        this.setDisabled(true);
        GetApi()
        .then(api => api.createFCCUserRole(
          this.props.fccId,
          {
            eMail: stateEmail,
            role: this.state.role,
            resourceUrl: window.location.href
          }
        ))
        .then((ur) => this.setState(prevState => ({
          email: '',
          role: 'Viewer' as Roles,
          shareUsers: {
            ...prevState.shareUsers,
            [ur.eMail]: ur,
          },
        })))
        .catch(error => this.context.useToasts.addToast(error, 'danger'))
        .finally(() => this.setDisabled(false))
      }
    } else {
      this.context.useToasts.addToast('User with email address has already been invited.', 'danger')
    }
  }

  private updateUserRole = ( email: string, role: Roles ) => {
    this.setDisabled(true);
    GetApi()
    .then(api => api.updateFCCUserRole(
      this.props.fccId,
      email,
      {
        ...this.state.shareUsers[email],
        role
      }
    ))
    .then((ur) => this.setState(prevState => ({
      shareUsers: {
        ...prevState.shareUsers,
        [ur.eMail]: ur,
      },
    })))
    .catch(error => this.context.useToasts.addToast(error, 'danger'))
    .finally(() => this.setDisabled(false))
  }

  private deleteUserRole = async ( email: string ) => {
    this.setDisabled(true);
    try {
      if (window.confirm('Are you sure you wish to remove the user role?')) {
        const newShareUsers = { ...this.state.shareUsers };
        delete newShareUsers[email];
        const api = await GetApi();
        await api.deleteFCCUserRole(this.props.fccId, email);
        this.setState({
          shareUsers: newShareUsers
        }, () => this.context.useToasts.addToast(`Successfully removed user role for ${email}.`, 'success', true))
      }
    } catch (error) {
      this.context.useToasts.addToast(error, 'danger')
    } finally {
      this.setDisabled(false)
    }
  }

  private setEmail = ( e: any ) => {
    e.persist()
    const isValid = ShareUserValidator.getFieldValidator('email')(emailId, e.target.value)
    this.setState({
      email: e.target.value,
      valid: isValid
    }, () => this.props.setValid(isValid))
  }
  
  private setRole = ( value: Roles ) => {
    this.setState({
      role: value,
    })
  }

  private setDisabled = ( value: boolean ) => {
    this.props.setDisabled(value);
    this.setState({
      disabled: value
    });
  }
}

interface EditProps extends InjectedProps {
  fccId: string
}

const Edit = ( props: EditProps ) => {
  const getData = async (): Promise<InnerEditProps> => {
    const api = await GetApi();
    const userRoles = await api.getFCCUserRoles(props.fccId);
    const shareUsers: ShareUsers = {};
    for (const u of userRoles) { shareUsers[u.eMail] = u; }
    return {
      ...props,
      email: '',
      role: 'Viewer' as Roles,
      shareUsers
    }
  }

  const renderSuccess = ( innerProps: InnerEditProps ) => <InnerEdit {...innerProps} />;

  return (
    <Pending
      getData={getData}
      renderSuccess={renderSuccess}
    />
  );
}

export interface EditModalProps extends WithModalProps {
  fccId: string
}

const EditModal = withModal(Edit);

export default EditModal;